import { IOptions } from '../../../common/common.types'
import { IAddMedicationAdministeredStateList } from '../../addMedicationAdministered/store/addMedicationAdministered.types'
import { IIntubationStateList } from '../../intubation/store/intubation.types'
import { IAirwayStateList } from '../../procedures/airway/store/airway.types'
import { ICardiacStateList } from '../../procedures/cardiac/store/cardiac.types'
import { IDrainStateList } from '../../procedures/drain/store/drain.types'
import { IHospitalNotifyStateList } from '../../procedures/hospitalNotify/store/hospitalNotify.types'
import { IInitiateIvStateList } from '../../procedures/initiateIv/store/initiateIv.types'
import { ILabsStateList } from '../../procedures/labs/store/labs.types'
import { IOperationsStateList } from '../../procedures/operations/store/operations.types'
import { ITitrateStateList } from '../../procedures/titrate/store/titrate.types'
import { IVentilatorStateList } from '../../procedures/ventilator/store/ventilator.types'
import { IItems } from '../../timeline/store/timeline.types'
import { IEKGEvent } from '../store/ekgImport.types'
import createMedicationProcedure, { MedicationProcedureObject } from './createMedicationProcedure.util'
import createAirwayProcedure, { AirwayProcedureObject } from './createAirwayProcedure.util'
import createCardiacProcedure, { CardiacProcedureObject } from './createCardiacProcedure.util'
import createDrainProcedure, { DrainProcedureObject } from './createDrainProcedure.util'
import createVentilatorProcedure, { VentilatorProcedureObject } from './createVentilatorProcedure.util'
import createInitiateIvProcedure, { InitiateIvProcedureObject } from './createInitiateIvProcedure.util'
import createHospitalNotifyProcedure, { HospitalNotifyProcedureObject } from './createHospitalNotifyProcedure.util'
import createIntubationProcedure, { IntubationProcedureObject } from './createIntubationProcedure.util'
import createOperationsProcedure, { OperationsProcedureObject } from './createOperationsProcedure.util'
import createLabsProcedure, { LabsProcedureObject } from './createLabsProcedure.util'
import createTitrateProcedure, { TitrateProcedureObject } from './createTitrateProcedure.util'
import createChildbirthProcedure, { ChildbirthProcedureObject } from './createChildbirthProcedure.util'
import { IChildbirthOBStateList } from '../../procedures/childbirthOB/store/childbirthOB.types'
import createExtricationProcedure, { ExtricationProcedureObject } from './createExtricationProcedure.util'
import { IExtricationStateList } from '../../procedures/extrication/store/extrication.types'
import { IImmobilizationStateList } from '../../procedures/immobilization/store/immobilization.types'
import createImmobilizationProcedure, { ImmobilizationProcedureObject } from './createImmobilizationProcedure.util'
import { IMedicalConsultStateList } from '../../procedures/medicalConsult/store/medicalConsult.types'
import createMedicalConsultProcedure, { MedicalConsultProcedureObject } from './createMedicalConsultProcedure.util'
import { IRescueStateList } from '../../procedures/rescue/store/rescue.types'
import createRescueProcedure, { RescueProcedureObject } from './createRescueProcedure.util'
import { IWoundCareStateList } from '../../procedures/woundCare/store/woundCare.types'
import createWoundCareProcedure, { WoundCareProcedureObject } from './createWoundCareProcedure.util'
import { IMedicationOptions } from '../../patientMedications/store/patientMedications.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { ICriticalCareStateList } from '../../procedures/criticalCare/store/criticalCare.types'
import createCriticalCareProcedure, { CriticalCareProcedureObject } from './createCriticalCareProcedure.util'

export interface ProcedureOptions {
  medicationsOptions: IMedicationOptions
  doseUnitOptions: IOptions
  airwayOptions: IOptions
  cardiacOptions: IOptions
  pacingModeOptions: IOptions
  defibTypeOptions: IOptions
  waveformTypeOptions: IOptions
  drainOptions: IOptions
  hospitalNotifyOptions: IOptions
  intubationOptions: IOptions
  initiateIvOptions: IOptions
  childbirthOptions: IOptions
  immobilizationOptions: IOptions
  medicalConsultOptions: IOptions
  woundCareOptions: IOptions
  criticalCareOptions: IOptions
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface CreateProcedureProps {
  procedureEvents: IEKGEvent[]
  procedureOptions: ProcedureOptions
  proceduresObject: ProceduresObject
  timelineItems: IItems
  ptaCutoff: null | Date
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface ProceduresObject {
  timelineItems: IItems
  addMedicationAdministeredStateList: IAddMedicationAdministeredStateList
  airwayStateList: IAirwayStateList
  cardiacStateList: ICardiacStateList
  drainStateList: IDrainStateList
  hospitalNotifyStateList: IHospitalNotifyStateList
  initiateIvStateList: IInitiateIvStateList
  intubationStateList: IIntubationStateList
  labsStateList: ILabsStateList
  operationsStateList: IOperationsStateList
  titrateStateList: ITitrateStateList
  ventilatorStateList: IVentilatorStateList
  childbirthStateList: IChildbirthOBStateList
  extricationStateList: IExtricationStateList
  immobilizationStateList: IImmobilizationStateList
  medicalConsultStateList: IMedicalConsultStateList
  rescueStateList: IRescueStateList
  woundCareStateList: IWoundCareStateList
  criticalCareStateList: ICriticalCareStateList
}

export const constructProceduresObject = (props: CreateProcedureProps): ProceduresObject => {
  const { procedureEvents, procedureOptions, proceduresObject, timelineItems, ptaCutoff } = props
  let newProceduresObject = proceduresObject

  procedureEvents.forEach((procedureEvent) => {
    const alreadyInTimeline = timelineItems.some((tItem) => tItem.key === procedureEvent.id.toString())
    if (!alreadyInTimeline) {
      newProceduresObject = addProcedureToProceduresObject(
        procedureEvent,
        procedureOptions,
        newProceduresObject,
        ptaCutoff
      )
    }
  })

  return newProceduresObject
}

const addProcedureToProceduresObject = (
  procedureEvent: IEKGEvent,
  procedureOptions: ProcedureOptions,
  proceduresObject: ProceduresObject,
  ptaCutoff: Date | null
): ProceduresObject => {
  const {
    medicationsOptions,
    doseUnitOptions,
    airwayOptions,
    cardiacOptions,
    pacingModeOptions,
    defibTypeOptions,
    waveformTypeOptions,
    drainOptions,
    initiateIvOptions,
    hospitalNotifyOptions,
    intubationOptions,
    childbirthOptions,
    immobilizationOptions,
    medicalConsultOptions,
    woundCareOptions,
    criticalCareOptions,
    nemsisCodeOptions,
  } = procedureOptions

  const procedureType = procedureEvent.procedure
  let airwayProcedureObject: AirwayProcedureObject | null
  let medicationProcedureObject: MedicationProcedureObject | null
  let ventilatorProcedureObject: VentilatorProcedureObject | null
  let cardiacProcedureObject: CardiacProcedureObject | null
  let drainProcedureObject: DrainProcedureObject | null
  let hospitalNotifyProcedureObject: HospitalNotifyProcedureObject | null
  let intubationProcedureObject: IntubationProcedureObject | null
  let initiateIvProcedureObject: InitiateIvProcedureObject | null
  let operationsProcedureObject: OperationsProcedureObject | null
  let labsProcedureObject: LabsProcedureObject | null
  let titrateProcedureObject: TitrateProcedureObject | null
  let childbirthProcedureObject: ChildbirthProcedureObject | null
  let extricationProcedureObject: ExtricationProcedureObject | null
  let immobilizationProcedureObject: ImmobilizationProcedureObject | null
  let medicalConsultProcedureObject: MedicalConsultProcedureObject | null
  let rescueProcedureObject: RescueProcedureObject | null
  let woundCareProcedureObject: WoundCareProcedureObject | null
  let criticalCareProcedureObject: CriticalCareProcedureObject | null

  switch (procedureType) {
    case 'Medication':
      medicationProcedureObject = createMedicationProcedure({
        procedureEvent,
        medicationsOptions,
        doseUnitOptions,
        ptaCutoff,
      })
      if (!medicationProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        addMedicationAdministeredStateList: [
          ...proceduresObject.addMedicationAdministeredStateList,
          medicationProcedureObject.addMedicationAdministeredState,
        ],
        timelineItems: [...proceduresObject.timelineItems, medicationProcedureObject.timelineItem],
      }

      return proceduresObject

    case 'Airway':
      airwayProcedureObject = createAirwayProcedure({
        procedureEvent,
        airwayOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })

      if (!airwayProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        airwayStateList: [...proceduresObject.airwayStateList, airwayProcedureObject.airwayState],
        timelineItems: [...proceduresObject.timelineItems, airwayProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Ventilator':
      ventilatorProcedureObject = createVentilatorProcedure({
        procedureEvent,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!ventilatorProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        ventilatorStateList: [...proceduresObject.ventilatorStateList, ventilatorProcedureObject.ventilatorState],
        timelineItems: [...proceduresObject.timelineItems, ventilatorProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Cardiac':
      cardiacProcedureObject = createCardiacProcedure({
        procedureEvent,
        cardiacOptions,
        pacingModeOptions,
        defibTypeOptions,
        waveformTypeOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })

      if (!cardiacProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        cardiacStateList: [...proceduresObject.cardiacStateList, cardiacProcedureObject.cardiacState],
        timelineItems: [...proceduresObject.timelineItems, cardiacProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Drain':
      drainProcedureObject = createDrainProcedure({
        procedureEvent,
        drainOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })

      if (!drainProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        drainStateList: [...proceduresObject.drainStateList, drainProcedureObject.drainState],
        timelineItems: [...proceduresObject.timelineItems, drainProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Initiate IV':
      initiateIvProcedureObject = createInitiateIvProcedure({
        procedureEvent,
        initiateIvOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })

      if (!initiateIvProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        initiateIvStateList: [...proceduresObject.initiateIvStateList, initiateIvProcedureObject.initiateIvState],
        timelineItems: [...proceduresObject.timelineItems, initiateIvProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Hosp. Notify':
      hospitalNotifyProcedureObject = createHospitalNotifyProcedure({
        procedureEvent,
        hospitalNotifyOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!hospitalNotifyProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        hospitalNotifyStateList: [
          ...proceduresObject.hospitalNotifyStateList,
          hospitalNotifyProcedureObject.hospitalNotifyState,
        ],
        timelineItems: [...proceduresObject.timelineItems, hospitalNotifyProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Intubation':
      intubationProcedureObject = createIntubationProcedure({
        procedureEvent,
        intubationOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!intubationProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        intubationStateList: [...proceduresObject.intubationStateList, intubationProcedureObject.intubationState],
        timelineItems: [...proceduresObject.timelineItems, intubationProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Operations':
      operationsProcedureObject = createOperationsProcedure({
        procedureEvent,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!operationsProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        operationsStateList: [...proceduresObject.operationsStateList, operationsProcedureObject.operationsState],
        timelineItems: [...proceduresObject.timelineItems, operationsProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Labs':
      labsProcedureObject = createLabsProcedure({
        procedureEvent,
        ptaCutoff,
      })
      if (!labsProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        labsStateList: [...proceduresObject.labsStateList, labsProcedureObject.labsState],
        timelineItems: [...proceduresObject.timelineItems, labsProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Titrate':
      titrateProcedureObject = createTitrateProcedure({
        procedureEvent,
        ptaCutoff,
      })
      if (!titrateProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        titrateStateList: [...proceduresObject.titrateStateList, titrateProcedureObject.titrateState],
        timelineItems: [...proceduresObject.timelineItems, titrateProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Childbirth':
      childbirthProcedureObject = createChildbirthProcedure({
        procedureEvent,
        childbirthOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!childbirthProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        childbirthStateList: [...proceduresObject.childbirthStateList, childbirthProcedureObject.childbirthState],
        timelineItems: [...proceduresObject.timelineItems, childbirthProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Extrication':
      extricationProcedureObject = createExtricationProcedure({
        procedureEvent,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!extricationProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        extricationStateList: [...proceduresObject.extricationStateList, extricationProcedureObject.extricationState],
        timelineItems: [...proceduresObject.timelineItems, extricationProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Immobilization':
      immobilizationProcedureObject = createImmobilizationProcedure({
        procedureEvent,
        immobilizationOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!immobilizationProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        immobilizationStateList: [
          ...proceduresObject.immobilizationStateList,
          immobilizationProcedureObject.immobilizationState,
        ],
        timelineItems: [...proceduresObject.timelineItems, immobilizationProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Medical Consult':
      medicalConsultProcedureObject = createMedicalConsultProcedure({
        procedureEvent,
        medicalConsultOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!medicalConsultProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        medicalConsultStateList: [
          ...proceduresObject.medicalConsultStateList,
          medicalConsultProcedureObject.medicalConsultState,
        ],
        timelineItems: [...proceduresObject.timelineItems, medicalConsultProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Rescue':
      rescueProcedureObject = createRescueProcedure({
        procedureEvent,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!rescueProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        rescueStateList: [...proceduresObject.rescueStateList, rescueProcedureObject.rescueState],
        timelineItems: [...proceduresObject.timelineItems, rescueProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Wound Care':
      woundCareProcedureObject = createWoundCareProcedure({
        procedureEvent,
        woundCareOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!woundCareProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        woundCareStateList: [...proceduresObject.woundCareStateList, woundCareProcedureObject.woundCareState],
        timelineItems: [...proceduresObject.timelineItems, woundCareProcedureObject.timelineItem],
      }
      return proceduresObject

    case 'Critical Care':
      criticalCareProcedureObject = createCriticalCareProcedure({
        procedureEvent,
        criticalCareOptions,
        ptaCutoff,
        nemsisCodeOptions,
      })
      if (!criticalCareProcedureObject) return proceduresObject

      proceduresObject = {
        ...proceduresObject,
        criticalCareStateList: [
          ...proceduresObject.criticalCareStateList,
          criticalCareProcedureObject.criticalCareState,
        ],
        timelineItems: [...proceduresObject.timelineItems, criticalCareProcedureObject.timelineItem],
      }
      return proceduresObject

    default:
      return proceduresObject
  }
}
